import React, { useState, useEffect, useRef } from "react";
import { Trash2, Copy, Check, X, Settings, GripVertical } from "lucide-react";

export default function DrawingActionBar({
  selectedDrawing,
  onUpdateDrawing,
  onDeleteDrawing,
  onDuplicateDrawing,
  onOpenSettings,
  onDeselect,
  position,
}) {
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [dragPos, setDragPos] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ mouseX: 0, mouseY: 0, posX: 0, posY: 0 });
  const barRef = useRef(null);

  const COLORS = ["#38bdf8", "#10b981", "#ef4444", "#f59e0b", "#c084fc", "#f43f5e", "#ffffff"];

  // Helper to get bounded coordinates inside parent chart container
  const getClampedPos = () => {
    const parent = barRef.current?.parentElement;
    const parentW = parent ? parent.clientWidth : window.innerWidth;
    const parentH = parent ? parent.clientHeight : window.innerHeight;
    const barW = barRef.current?.offsetWidth || 440;
    const barH = barRef.current?.offsetHeight || 44;

    if (dragPos) {
      return {
        left: Math.max(10, Math.min(dragPos.x, parentW - barW - 10)),
        top: Math.max(10, Math.min(dragPos.y, parentH - barH - 10)),
      };
    }

    // Default auto positioning based on click coordinate position
    let rawX = position?.x ?? 200;
    let rawY = (position?.y ?? 100) - 56;

    if (rawY < 10) rawY = (position?.y ?? 100) + 20;

    // Shift left if toolbar would exceed container right boundary (so Trash & X buttons are NEVER hidden!)
    const clampedX = Math.max(10, Math.min(rawX - 60, parentW - barW - 15));
    const clampedY = Math.max(10, Math.min(rawY, parentH - barH - 10));

    return { left: clampedX, top: clampedY };
  };

  // Drag handlers
  const handleDragStart = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const currentPos = getClampedPos();
    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      posX: currentPos.left,
      posY: currentPos.top,
    };
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      const dx = e.clientX - dragStartRef.current.mouseX;
      const dy = e.clientY - dragStartRef.current.mouseY;

      const parent = barRef.current?.parentElement;
      const parentW = parent ? parent.clientWidth : window.innerWidth;
      const parentH = parent ? parent.clientHeight : window.innerHeight;
      const barW = barRef.current?.offsetWidth || 440;
      const barH = barRef.current?.offsetHeight || 44;

      let newX = dragStartRef.current.posX + dx;
      let newY = dragStartRef.current.posY + dy;

      newX = Math.max(10, Math.min(newX, parentW - barW - 10));
      newY = Math.max(10, Math.min(newY, parentH - barH - 10));

      setDragPos({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  if (!selectedDrawing) return null;

  const currentStylePos = getClampedPos();

  return (
    <div
      ref={barRef}
      style={currentStylePos}
      className={`absolute z-40 flex items-center gap-1.5 p-1.5 bg-slate-900/95 border border-slate-700/90 rounded-xl shadow-2xl backdrop-blur-md text-xs font-sans animate-fadeIn select-none pointer-events-auto transition-shadow ${
        isDragging ? "ring-2 ring-cyan-500/50 shadow-cyan-500/20 cursor-grabbing" : ""
      }`}
    >
      {/* Drag Handle (Grip icon - Kéo di chuyển thanh công cụ) */}
      <div
        onMouseDown={handleDragStart}
        className="p-1 text-slate-400 hover:text-slate-100 hover:bg-slate-800/80 rounded-lg cursor-grab active:cursor-grabbing transition flex items-center justify-center"
        title="Bấm giữ và kéo để di chuyển thanh công cụ"
      >
        <GripVertical className="w-4 h-4" />
      </div>

      <div className="h-4 w-px bg-slate-800" />

      {/* Settings Modal Trigger */}
      {onOpenSettings && (
        <>
          <button
            onClick={() => onOpenSettings(selectedDrawing)}
            className="p-1.5 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 transition cursor-pointer flex items-center gap-1"
            title="Cài đặt thông số chi tiết (Định dạng, Mức tỷ lệ, Tọa độ)"
          >
            <Settings className="w-4 h-4" />
          </button>
          <div className="h-4 w-px bg-slate-800" />
        </>
      )}

      {/* Color Picker Swatch */}
      <div className="relative">
        <button
          onClick={() => setIsColorPickerOpen(!isColorPickerOpen)}
          className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-300 transition cursor-pointer flex items-center gap-1"
          title="Đổi màu nét vẽ"
        >
          <div
            className="w-4 h-4 rounded-full border border-slate-600 shadow-sm"
            style={{ backgroundColor: selectedDrawing.color || "#38bdf8" }}
          />
        </button>

        {isColorPickerOpen && (
          <div className="absolute left-0 top-8 bg-slate-900 border border-slate-700 rounded-xl p-2 shadow-2xl flex items-center gap-1.5 z-50 animate-fadeIn">
            {COLORS.map((c) => (
              <button
                key={c}
                onClick={() => {
                  onUpdateDrawing({ ...selectedDrawing, color: c });
                  setIsColorPickerOpen(false);
                }}
                className="w-5 h-5 rounded-full border border-slate-700 hover:scale-110 transition cursor-pointer flex items-center justify-center"
                style={{ backgroundColor: c }}
              >
                {selectedDrawing.color === c && <Check className="w-3 h-3 text-slate-950 stroke-[3]" />}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="h-4 w-px bg-slate-800" />

      {/* Line Thickness */}
      <select
        value={selectedDrawing.width || 2}
        onChange={(e) => onUpdateDrawing({ ...selectedDrawing, width: parseInt(e.target.value) || 1 })}
        className="bg-slate-950 border border-slate-800 rounded-lg px-2 py-1 text-slate-300 text-[11px] focus:outline-none focus:border-cyan-500 cursor-pointer"
        title="Độ dày nét vẽ"
      >
        <option value="1">─ 1px</option>
        <option value="2">━ 2px</option>
        <option value="3">█ 3px</option>
        <option value="4">█ 4px</option>
      </select>

      {/* Line Style (Solid / Dashed / Dotted) */}
      <select
        value={selectedDrawing.style || 0}
        onChange={(e) => onUpdateDrawing({ ...selectedDrawing, style: parseInt(e.target.value) || 0 })}
        className="bg-slate-950 border border-slate-800 rounded-lg px-2 py-1 text-slate-300 text-[11px] focus:outline-none focus:border-cyan-500 cursor-pointer"
        title="Kiểu nét vẽ"
      >
        <option value="0">── Nét Liền</option>
        <option value="2">-- Nét Đứt</option>
        <option value="1">·· Nét Chấm</option>
      </select>

      <div className="h-4 w-px bg-slate-800" />

      {/* Duplicate Button */}
      <button
        onClick={() => onDuplicateDrawing(selectedDrawing)}
        className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-slate-200 transition cursor-pointer"
        title="Nhân bản nét vẽ (Clone)"
      >
        <Copy className="w-4 h-4" />
      </button>

      {/* Delete / Trash Button (Box Xóa chuẩn TradingView) */}
      <button
        onClick={() => onDeleteDrawing(selectedDrawing.id)}
        className="p-1.5 rounded-lg bg-rose-500/10 hover:bg-rose-500/25 text-rose-400 border border-rose-500/30 transition cursor-pointer flex items-center justify-center"
        title="Xóa nét vẽ này (Phím Delete)"
      >
        <Trash2 className="w-4 h-4 text-rose-400" />
      </button>

      <div className="h-4 w-px bg-slate-800" />

      {/* Deselect / Close */}
      <button
        onClick={onDeselect}
        className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-500 hover:text-slate-300 transition cursor-pointer"
        title="Bỏ chọn"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}


